// Documentazione API — sezioni "TelephonyDocs". JSX estratto verbatim da DeveloperSettings.tsx.
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { DocSection, Endpoint, CodeBlock } from './primitives';
import type { ReactElement } from 'react';

interface LegacyToV2Row { legacy: string; v2: string; note: string }

const LEGACY_TO_V2_ROWS: LegacyToV2Row[] = [
  {
    legacy: 'POST/PUT/DELETE /extensions · POST /extensions/:id/reset-password',
    v2: '/api/v2/voip/extensions · /:external_id/reset-password · /sync · /blf-list',
    note: "Stesso modello; l'identificativo è l'external_id dell'integratore.",
  },
  {
    legacy: 'GET/POST/PUT/DELETE /trunks',
    v2: '/api/v2/voip/trunks · /sync · /:external_id/status',
    note: 'Body validati con i DTO condivisi SipTrunkV2CreateRequest / SipTrunkUpdateRequest (400 con dettaglio per campo, sync con errori per elemento); 409 DID_ALREADY_ASSIGNED sui DID in conflitto.',
  },
  {
    legacy: 'Trunk: aiAgentRef · aiFailoverExtension · aiTimePolicy',
    v2: '/api/v2/voip/inbound-routes',
    note: "Il trunk non porta più configurazione AI. L'agente è la destinazione della rotta inbound (destination_type internal_ai_agent / external_ai_agent, destination_id = agente); aiFailoverExtension → failover_destination_type/_id; aiTimePolicy → time_condition_id (/api/v2/voip/time-conditions).",
  },
  {
    legacy: 'POST /cdr (ingestione CDR)',
    v2: '— non necessario',
    note: 'I CDR sono prodotti dal piano voce via ESL/FreeSWITCH: nessuna ingestione da terzi. Lettura via GET /api/v2/voip/cdr, /cdr/:call_uuid, /cdr/stats, /cdr/export.',
  },
  {
    legacy: 'GET /cdr · /cdr/export · /cdr/stats',
    v2: '/api/v2/voip/cdr · /cdr/export · /cdr/stats',
    note: 'Scope voip:read / cdr:export.',
  },
  {
    legacy: 'POST /ai-routing/evaluate',
    v2: '— sostituito dal dialplan',
    note: 'La decisione di instradamento (orari, failover, agente) è eseguita dal dialplan FreeSWITCH in base a inbound routes + time conditions; nessun endpoint di valutazione sincrona. Per simulare una rotta in uscita: POST /api/v2/voip/outbound-routes/test-pattern.',
  },
  {
    legacy: 'GET/POST/PUT/DELETE /webhooks · /:id/deliveries · /:id/test',
    v2: '/api/v2/voip/webhooks (stessi sotto-percorsi) · /events',
    note: 'Firma HMAC sul body; eventi elencati nella sezione Webhooks.',
  },
  {
    legacy: 'GET /health',
    v2: '/api/v2/voip/health',
    note: 'Stato del backend API v2, senza API key.',
  },
];

export function renderTelephonyDocSection(section: string): ReactElement | null {
  switch (section) {
      case 'queues':
        return (
          <DocSection id="queues">
            <h3 className="text-lg font-semibold">Queues API</h3>
            <p className="text-sm text-muted-foreground">
              Code di chiamata (ACD). Base: <code className="bg-muted px-1 rounded">/api/v2/voip/queues</code>.
              Lettura: <code className="bg-muted px-1 rounded">voip:read</code>; scrittura e gestione agenti: <code className="bg-muted px-1 rounded">voip:write</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/queues"
              desc="Lista code del tenant. Query: store_id (match raw sulla colonna call_queues.store_id; passare l'UUID interno), enabled (bool), limit (default 50), offset (default 0). meta = { total, limit, offset }."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/queues/:id"
              desc="Dettaglio coda inclusi agents[], strategy, max_wait_time, callback config."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/queues/:id/agents"
              desc="Lista agenti associati alla coda con stato corrente (logged_in, paused, on_call)."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Write</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/queues"
              desc="Crea coda. Obbligatori a livello di route: name, extension. strategy ha default 'ring-all' lato server se omessa."
              scope="voip:write"
            />
            <Endpoint
              method="PUT"
              path="/api/v2/voip/queues/:id"
              desc="Aggiorna campi mutabili (name, strategy, max_wait_time, agents, moh_sound, settings, callback_*)."
              scope="voip:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/queues/:id"
              desc="Elimina la coda dal tenant e dalla configurazione FreeSWITCH (mod_callcenter)."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/queues/:id/duplicate"
              desc="Clona la coda con strategy, agents, settings. Aggiunge suffisso (Copy)."
              scope="voip:write"
            />

            <h4 className="text-sm font-semibold mt-4">Agent control</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/queues/:id/agents/:agent_id/login"
              desc="Login agente nella coda. :agent_id è l'UUID della riga in queue_agents (NON l'extension). Imposta status='Available', state='Waiting'."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/queues/:id/agents/:agent_id/logout"
              desc="Logout agente dalla coda. :agent_id è l'UUID di queue_agents. Imposta status='Logged Out', state='Idle'."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/queues/:id/agents/:agent_id/pause"
              desc="Mette in pausa l'agente: imposta status='On Break', state='Idle'. :agent_id è l'UUID di queue_agents. Nessun campo del body è richiesto/letto dall'attuale implementazione."
              scope="voip:write"
            />

            <CodeBlock title="POST /queues — Create Request">{`{
  "name": "Supporto",
  "extension": "5000",
  "strategy": "ring-all",          // ring-all | longest-idle-agent | round-robin | top-down | agent-with-least-talk-time | agent-with-fewest-calls | sequentially-by-agent-order | random
  "store_id": "uuid-store",        // UUID interno della tabella stores (NON store_id custom)
  "description": "Coda supporto clienti",
  "max_wait_time": 300,            // secondi
  "agents": ["1001", "1002"],      // array di extension
  "moh_sound": "local_stream://moh",
  "enabled": true,
  "settings": { "wrap_up_time": 10 },
  "callback_enabled": true,
  "callback_max_wait_seconds": 600
}`}</CodeBlock>

            <CodeBlock title="GET /queues/:id/agents — Response">{`{
  "success": true,
  "data": [
    {
      "id": "uuid-queue-agent",          // <-- usare QUESTO come :agent_id in login/logout/pause
      "queue_id": "uuid-queue",
      "agent_name": "John Doe",
      "agent_extension": "1001",
      "status": "Available",             // Available | On Break | Logged Out | ...
      "state": "Waiting",                // Waiting | Idle | In a queue call | ...
      "tier_level": 1,
      "tier_position": 1,
      "updated_at": "2026-05-04T09:00:00Z"
    }
  ]
}`}</CodeBlock>

            <CodeBlock title="POST /queues/:id/agents/:agent_id/pause — Response">{`# Nessun body richiesto. L'effetto è sempre lo stesso: status='On Break', state='Idle'.
# Non esiste ancora un endpoint dedicato di "unpause": usare /login per riportare l'agente ad Available.

{
  "success": true,
  "data": {
    "id": "uuid-queue-agent",
    "queue_id": "uuid-queue",
    "agent_name": "John Doe",
    "status": "On Break",
    "state": "Idle",
    "updated_at": "2026-05-04T09:05:00Z"
  }
}`}</CodeBlock>
          </DocSection>
        );

      case 'ringgroups':
        return (
          <DocSection id="ringgroups">
            <h3 className="text-lg font-semibold">Ring Groups API</h3>
            <p className="text-sm text-muted-foreground">
              Gruppi di squillo. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/ring-groups</code>.
              Lettura: <code className="bg-muted px-1 rounded">voip:read</code>; scrittura: <code className="bg-muted px-1 rounded">voip:write</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/ring-groups"
              desc="Lista ring groups con filtri opzionali store_id, enabled, limit, offset."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/ring-groups/:id"
              desc="Dettaglio ring group con members[] e ring_time."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/ring-groups/:id/members"
              desc="Lista membri (extension destinations) del gruppo."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Write</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/ring-groups"
              desc="Crea ring group. Obbligatori a livello di route: name, extension. strategy/ring_time hanno default lato server."
              scope="voip:write"
            />
            <Endpoint
              method="PUT"
              path="/api/v2/voip/ring-groups/:id"
              desc="Aggiorna ring group (name, strategy, ring_time, members, moh_sound, enabled)."
              scope="voip:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/ring-groups/:id"
              desc="Elimina il gruppo."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/ring-groups/:id/duplicate"
              desc="Clona ring group con members. Aggiunge suffisso (Copy)."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/ring-groups/:id/members"
              desc="Aggiunge un membro al ring group. Body: { extension_id (UUID dell'extension, OBBLIGATORIO), priority?: number=1, ring_delay?: number=0, ring_timeout?: number=30 }."
              scope="voip:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/ring-groups/:id/members/:member_id"
              desc="Rimuove un membro per UUID interno."
              scope="voip:write"
            />

            <CodeBlock title="POST /ring-groups — Create Request">{`{
  "name": "Vendite",
  "extension": "6000",
  "strategy": "ring-all",          // valore di default lato server se omesso
  "ring_time": 30,                 // secondi totali di squillo (default 30)
  "members": [                     // facoltativo: snapshot JSON salvato sulla riga ring_groups
    { "extension_id": "uuid-ext-1001", "priority": 1, "ring_delay": 0,  "ring_timeout": 30 },
    { "extension_id": "uuid-ext-1002", "priority": 2, "ring_delay": 5,  "ring_timeout": 30 }
  ],
  "moh_sound": "local_stream://moh",
  "enabled": true
}`}</CodeBlock>

            <CodeBlock title="POST /ring-groups/:id/members — Request (riga in ring_group_members)">{`{
  "extension_id": "uuid-of-extension",   // OBBLIGATORIO (UUID dell'extension, NON la sua stringa numerica)
  "priority":     3,                     // default 1
  "ring_delay":   10,                    // secondi di ritardo prima di iniziare a squillare
  "ring_timeout": 30                     // secondi di squillo prima di rinunciare
}`}</CodeBlock>
          </DocSection>
        );

      case 'inbound':
        return (
          <DocSection id="inbound">
            <h3 className="text-lg font-semibold">Inbound Routes API</h3>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/inbound-routes</code> — scope: <code>voip:read</code> / <code>voip:write</code>.</p>
            <Endpoint method="GET" path="/api/v2/voip/inbound-routes" desc="Lista rotte inbound del tenant. Query: store_id, did_number (match esatto), enabled, limit, offset. Ordinata per priority ASC." scope="voip:read" />
            <Endpoint method="GET" path="/api/v2/voip/inbound-routes/:id" desc="Dettaglio rotta con destinazione primaria, time_condition_id e failover_destination_*." scope="voip:read" />
            <Endpoint method="POST" path="/api/v2/voip/inbound-routes" desc="Crea rotta inbound. Obbligatori: name, did_number, destination_type, destination_id." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/inbound-routes/:id" desc="Aggiorna rotta (campi parziali ammessi)." scope="voip:write" />
            <Endpoint method="DELETE" path="/api/v2/voip/inbound-routes/:id" desc="Elimina rotta." scope="voip:write" />
            <Endpoint method="POST" path="/api/v2/voip/inbound-routes/:id/duplicate" desc="Duplica rotta. NOTA: l'INSERT del clone NON copia i campi failover_destination_type/_id/_data (verranno NULL sulla nuova rotta); ricreali via PUT se servono." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/inbound-routes/reorder" desc="Riordina per priority. Body { orderedIds: [] }: ogni UUID deve appartenere al tenant (403 altrimenti); priority assegnata = (idx+1)*10." scope="voip:write" />
            <p className="text-sm text-muted-foreground mt-2"><code className="bg-muted px-1 rounded">destination_type</code> supportati: <code>extension</code>, <code>queue</code>, <code>ring_group</code>, <code>ivr_menu</code>, <code>voicemail</code>, <code>external</code>.</p>
            <CodeBlock title="Create Route Request (with failover)">{`{
  "name": "Main Line",
  "did_number": "+1234567890",
  "destination_type": "ivr_menu",
  "destination_id": "uuid-of-ivr-menu",
  "priority": 100,
  "enabled": true,
  "time_condition_id": "uuid-or-null",
  "failover_destination_type": "voicemail",
  "failover_destination_id": "uuid-of-voicemail-box",
  "failover_destination_data": { "greeting": "default" }
}`}</CodeBlock>
            <CodeBlock title="Reorder Request">{`{
  "orderedIds": ["uuid-route-1", "uuid-route-2", "uuid-route-3"]
}`}</CodeBlock>
            <p className="text-xs text-muted-foreground">I 3 campi <code>failover_destination_*</code> sono opzionali; passa <code>null</code> per rimuovere il failover. Vengono attivati quando la destinazione primaria non è raggiungibile (no answer / busy / failed) o quando la time-condition associata indica fascia fuori-orario.</p>
          </DocSection>
        );

      case 'outbound':
        return (
          <DocSection id="outbound">
            <h3 className="text-lg font-semibold">Outbound Routes API</h3>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/outbound-routes</code> — scope: <code>voip:read</code> / <code>voip:write</code>.</p>
            <Endpoint method="GET" path="/api/v2/voip/outbound-routes" desc="Lista rotte outbound. Query: store_id, enabled, pattern (substring match sul campo regex), limit, offset. Ordinata per priority ASC." scope="voip:read" />
            <Endpoint method="GET" path="/api/v2/voip/outbound-routes/:id" desc="Dettaglio rotta (riga di outbound_routes). NON include i trunks associati: per ottenerli usare GET /:id/trunks." scope="voip:read" />
            <Endpoint method="POST" path="/api/v2/voip/outbound-routes" desc="Crea rotta. Obbligatori: name, pattern. Trunks è opzionale ma raccomandato." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/outbound-routes/:id" desc="Aggiorna rotta (campi parziali)." scope="voip:write" />
            <Endpoint method="DELETE" path="/api/v2/voip/outbound-routes/:id" desc="Elimina rotta. Bloccato con HTTP 400 ed error_code='LAST_ROUTE' se è l'ultima rotta outbound del tenant (per impedire numerazione orfana, in tal caso disabilitarla)." scope="voip:write" />
            <Endpoint method="POST" path="/api/v2/voip/outbound-routes/:id/duplicate" desc="Duplica rotta + associazioni trunk. Aggiunge suffisso (Copy)." scope="voip:write" />
            <Endpoint method="GET" path="/api/v2/voip/outbound-routes/:id/trunks" desc="Trunks associati alla rotta in ordine di priority." scope="voip:read" />
            <Endpoint method="PUT" path="/api/v2/voip/outbound-routes/:id/trunks" desc="Sostituisce l'intero set di trunk. Body { trunks: [{ trunk_id, priority }] }." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/outbound-routes/reorder" desc="Riordina per priority. Body { orderedIds: [] }; assegna priority a partire da 100, incremento +1 per posizione (100, 101, 102, ...). Le rotte con is_standard_evergreen=true sono escluse dall'update. Invalida la cache trunk e rigenera i context FreeSWITCH del tenant." scope="voip:write" />
            <Endpoint method="POST" path="/api/v2/voip/outbound-routes/test-pattern" desc="Test del dial_pattern delle rotte enabled contro un numero. Body { testNumber }. Wildcard supportati: X=cifra, .=any char, *=zero o più. Risposta: { matches: [{ route, matchType: 'exact'|'pattern', matchDetails, transformedNumber }], totalRoutes }. transformedNumber applica strip_digits/prefix/add_digits della rotta; matches[] è ordinato per priority ASC. Read-only: richiede solo voip:read." scope="voip:read" />
            <CodeBlock title="Create Outbound Route">{`{
  "name": "International Calls",
  "pattern": "^00[1-9]",
  "replacement": "",
  "priority": 50,
  "enabled": true,
  "trunks": [{ "trunk_id": "uuid", "priority": 1 }]
}`}</CodeBlock>
            <CodeBlock title="Set Trunks Request">{`{
  "trunks": [
    { "trunk_id": "uuid-trunk-1", "priority": 1 },
    { "trunk_id": "uuid-trunk-2", "priority": 2 }
  ]
}`}</CodeBlock>
            <CodeBlock title="POST /test-pattern — Request">{`{
  "testNumber": "+39123456789"
}`}</CodeBlock>
            <CodeBlock title="POST /test-pattern — Response">{`{
  "success": true,
  "data": {
    "matches": [
      {
        "route": { "id": "uuid", "name": "International Calls", "dial_pattern": "00X.", "priority": 100, "strip_digits": 2, "prefix": "+", "add_digits": null, "enabled": true /* ...altri campi della rotta */ },
        "matchType": "pattern",                  // 'exact' se dial_pattern === testNumber, altrimenti 'pattern'
        "matchDetails": "Pattern: 00X. → /00\\\\d./",
        "transformedNumber": "+39123456789"      // applicato strip_digits → prefix → add_digits
      }
    ],
    "totalRoutes": 7                              // numero totale di rotte enabled valutate
  }
}
// Nessun campo boolean 'matched' o 'selected'; la prima entry di matches[] è la rotta a priority più alta (priority ASC) — usare matches.length > 0 per sapere se qualcosa ha matchato.`}</CodeBlock>
            <CodeBlock title="PUT /reorder — Request">{`{
  "orderedIds": [
    "uuid-route-A",   // diventa priority=100
    "uuid-route-B",   // diventa priority=101
    "uuid-route-C"    // diventa priority=102
  ]
}
// Le rotte standard evergreen (is_standard_evergreen=true) sono saltate in silenzio.`}</CodeBlock>
          </DocSection>
        );

      case 'ivr':
        return (
          <DocSection id="ivr">
            <h3 className="text-lg font-semibold">IVR Menus API</h3>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/ivr-menus</code> — scope: <code>voip:read</code> / <code>voip:write</code>.</p>
            <Endpoint method="GET" path="/api/v2/voip/ivr-menus" desc="Lista IVR menu del tenant. Nessun filtro server-side: ritorna sempre tutti i menu del tenant. meta = { total, timestamp }." scope="voip:read" />
            <Endpoint method="GET" path="/api/v2/voip/ivr-menus/:id" desc="Dettaglio menu con options (mappa digit→action) e audio prompts." scope="voip:read" />
            <Endpoint method="POST" path="/api/v2/voip/ivr-menus" desc="Crea menu. Obbligatori: name, extension. timeout default 5000ms." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/ivr-menus/:id" desc="Aggiorna menu, options, prompts (campi parziali)." scope="voip:write" />
            <Endpoint method="DELETE" path="/api/v2/voip/ivr-menus/:id" desc="Elimina menu. Restituisce 404 se l'id non appartiene al tenant." scope="voip:write" />
            <CodeBlock title="Create IVR Menu">{`{
  "name": "Menu principale",
  "extension": "9000",
  "description": "Menu di benvenuto",
  "greeting_sound": "ivr/welcome.wav",
  "invalid_sound": "ivr/invalid.wav",
  "exit_sound": "ivr/goodbye.wav",
  "timeout": 5,
  "max_failures": 3,
  "inter_digit_timeout": 2000,
  "max_timeouts": 3,
  "options": {
    "1": { "action": "queue",     "destination": "uuid-queue",     "description": "Supporto" },
    "2": { "action": "extension", "destination": "1001",           "description": "Commerciale" },
    "0": { "action": "voicemail", "destination": "uuid-vm-box",    "description": "Lascia messaggio" }
  },
  "timeout_action": { "type": "repeat",  "destination": "" },
  "invalid_action": { "type": "hangup",  "destination": "" },
  "enabled": true,
  "background_music": "none",
  "tts_voice": "nova",
  "tts_language": "it",
  "flow_type": "static"
}`}</CodeBlock>
            <p className="text-xs text-muted-foreground">Campi richiesti: <code>name</code>, <code>extension</code>. <code>timeout</code> è in <strong>secondi</strong> (range 1–60), <code>inter_digit_timeout</code> è in millisecondi. Le <code>options</code> sono un oggetto map <code>digit → action</code> dove ogni action ha <code>action</code> ∈ <code>extension</code> | <code>queue</code> | <code>ring_group</code> | <code>voicemail</code> | <code>submenu</code> | <code>conference</code> | <code>hangup</code> | <code>repeat</code> | <code>collect_digits</code> e <code>destination</code> (UUID o numero/identificatore della risorsa). I campi <code>timeout_action</code> / <code>invalid_action</code> usano un set ridotto: <code>extension</code> | <code>queue</code> | <code>voicemail</code> | <code>hangup</code> | <code>repeat</code>. Per IVR AI passa <code>flow_type: "ai_receptionist"</code> + <code>ai_agent_id</code>; per editor visuale a nodi usa <code>flow_type: "flow_visual"</code>. Un IVR menu può essere referenziato come <code>destination_type: ivr_menu</code> in un Inbound Route; la cancellazione è rifiutata se almeno una rotta lo referenzia.</p>
          </DocSection>
        );

      case 'time-conditions':
        return (
          <DocSection id="time-conditions">
            <h3 className="text-lg font-semibold">Time Conditions API</h3>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/time-conditions</code> — scope: <code>voip:read</code> / <code>voip:write</code>.</p>
            <Endpoint method="GET" path="/api/v2/voip/time-conditions" desc="Lista time conditions del tenant. Filtri: enabled (bool), store_id (UUID validato; 400 se non valido). meta.filters echo dei filtri applicati." scope="voip:read" />
            <Endpoint method="GET" path="/api/v2/voip/time-conditions/:id" desc="Dettaglio condizione: business_hours[], holidays[], branch business/after-hours/holiday." scope="voip:read" />
            <Endpoint method="POST" path="/api/v2/voip/time-conditions" desc="Crea condizione. Unico campo richiesto a livello di route: name. business_hours[], timezone, holidays[] e i branch *_action / *_destination sono FORTEMENTE consigliati ma non validati lato API (la validazione di forma è demandata al consumatore/UI)." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/time-conditions/:id" desc="Aggiorna fasce e branch (campi parziali ammessi). 404 se la condizione non appartiene al tenant." scope="voip:write" />
            <Endpoint method="DELETE" path="/api/v2/voip/time-conditions/:id" desc="Elimina condizione. 404 se non appartiene al tenant. Nessun reference-check verso inbound_routes lato API." scope="voip:write" />
            <CodeBlock title="Create Time Condition">{`{
  "name": "Orario ufficio",
  "description": "Lun-Ven 09:00-13:00 / 14:30-18:30",
  "timezone": "Europe/Rome",
  "business_hours": [
    { "days_of_week": [1,2,3,4,5], "start_time": "09:00", "end_time": "13:00" },
    { "days_of_week": [1,2,3,4,5], "start_time": "14:30", "end_time": "18:30" }
  ],
  "business_hours_action": "queue",
  "business_hours_destination": "uuid-queue",
  "after_hours_action": "voicemail",
  "after_hours_destination": "uuid-vm-box",
  "holiday_action": "voicemail",
  "holiday_destination": "uuid-vm-box",
  "holidays": [],
  "enabled": true
}`}</CodeBlock>
            <p className="text-xs text-muted-foreground">Schema: <code>business_hours</code> è un array di fasce orarie (<code>days_of_week</code> 0–6, <code>start_time</code>/<code>end_time</code> in <code>HH:MM</code>). <code>business_hours_action</code>/<code>after_hours_action</code>/<code>holiday_action</code> ∈ <code>extension</code> | <code>ring_group</code> | <code>queue</code> | <code>ivr</code> | <code>voicemail</code> | <code>external</code> | <code>internal_ai_agent</code> | <code>hangup</code> | <code>continue</code>. Il rispettivo <code>*_destination</code> contiene l'UUID della risorsa interna (oppure il numero/URI per <code>external</code>; omesso per <code>hangup</code>/<code>continue</code>). I campi <code>*_ai_error_fallback</code> sono esposti in lettura sulle response di <code>GET</code> ma non sono ancora accettati come input da <code>POST</code>/<code>PUT</code>. Il riferimento a una time-condition si imposta su un Inbound Route con il campo <code>time_condition_id</code>.</p>
          </DocSection>
        );

      case 'calls':
        return (
          <DocSection id="calls">
            <h3 className="text-lg font-semibold">Active Calls API</h3>
            <p className="text-sm text-muted-foreground">
              Controllo chiamate live via FreeSWITCH ESL. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/active-calls</code>.
              Lettura: <code className="bg-muted px-1 rounded">voip:read</code> oppure <code className="bg-muted px-1 rounded">calls:control</code>.
              Tutte le operazioni di scrittura/controllo richiedono <code className="bg-muted px-1 rounded">calls:control</code>.
              Ogni POST verifica l'ownership della call_uuid (live via unifiedCallEventService oppure CDR per il tenant): risposta 404 <code>CALL_NOT_FOUND</code> se la chiamata non appartiene al tenant.
              Risposta 503 <code>ESL_NOT_AVAILABLE</code> se FreeSWITCH non è connesso.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/active-calls"
              desc="Lista chiamate attive del tenant (CDR con end_time IS NULL). Query: limit (default 50), offset (default 0). Ordinata per start_time DESC."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Originate</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/originate"
              desc="Click-to-call. Obbligatori: caller_extension (interno, ^[0-9*#]{2,10}$) e callee_number (^\+?[0-9*#]{2,20}$). Il dominio SIP è derivato dal tenant autenticato: il campo domain è deprecato e, se presente e diverso, risponde 400 ORIGINATE_DOMAIN_MISMATCH. caller_extension deve appartenere al tenant (403 ACCESS_DENIED altrimenti). Restituisce 201 con call_uuid generato."
              scope="calls:control"
            />

            <h4 className="text-sm font-semibold mt-4">In-call control</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/hangup"
              desc="Termina la chiamata. Body opzionale: { cause } (default NORMAL_CLEARING)."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/transfer"
              desc="Transfer cieco. Body: { destination, type? } (type default 'blind')."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/hold"
              desc="Hold/unhold. Body: { hold: true|false } (default true)."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/mute"
              desc="Mute/unmute lato leg corrente. Body: { mute: true|false } (default true)."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/record"
              desc="Avvia o ferma la registrazione. Body: { record: true|false }. La start ritorna recording_path."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/attended-transfer"
              desc="Attended transfer multi-step. Body: { action: 'start'|'complete'|'cancel', destination?, consultUuid? }. 'start' richiede destination; 'complete'/'cancel' richiedono consultUuid (validato per tenant)."
              scope="calls:control"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/active-calls/:call_uuid/conference"
              desc="Crea conferenza a 3 con un terzo numero. Body: { thirdParty }."
              scope="calls:control"
            />

            <CodeBlock title="POST /active-calls/originate — Request / Response">{`# Request
{
  "caller_extension": "1001",
  "callee_number": "+1234567890",
  "campaign_id": "uuid-campagna",  // opzionale: lega la chiamata alla campagna (CDR + call_status)
  "options": {                     // opzionale, campi tipizzati (chiavi sconosciute → 400)
    "caller_id": "+390000000000",  // caller ID (max 80 char, senza , { } ' " a-capo)
    "timeout": 60,                 // 1..300 secondi
    "recording": true
  }
}

# 201 Response
{
  "success": true,
  "data": {
    "call_uuid": "fs-call-uuid",
    "caller_extension": "1001",
    "callee_number": "+1234567890",
    "domain": "tenant.sip.easyflowai.it",
    "campaign_id": "uuid-campagna",   // null se non inviato
    "status": "originated"
  }
}`}</CodeBlock>

            <CodeBlock title="POST /active-calls/:call_uuid/transfer — Request / Response">{`# Request
{ "destination": "1002", "type": "blind" }

# Response
{
  "success": true,
  "data": { "call_uuid": "...", "destination": "1002", "type": "blind", "status": "transferred" }
}`}</CodeBlock>

            <CodeBlock title="POST /active-calls/:call_uuid/attended-transfer — Flussi">{`# Step 1: start
POST { "action": "start", "destination": "1002" }
→ { "data": { "consultUuid": "consult-uuid", "status": "consulting" } }

# Step 2a: complete (collega le due gambe)
POST { "action": "complete", "consultUuid": "consult-uuid" }
→ { "data": { "success": true, "status": "completed" } }

# Step 2b: cancel (annulla e ritorna alla chiamata originale)
POST { "action": "cancel", "consultUuid": "consult-uuid" }
→ { "data": { "success": true, "status": "cancelled" } }`}</CodeBlock>

            <CodeBlock title="POST /active-calls/:call_uuid/record — Response">{`# record: true
{
  "success": true,
  "data": {
    "call_uuid": "...",
    "record": true,
    "recording_path": "/var/lib/freeswitch/recordings/...wav",
    "status": "recording_started"
  }
}

# record: false
{ "success": true, "data": { "call_uuid": "...", "record": false, "status": "recording_stopped" } }`}</CodeBlock>
          </DocSection>
        );

      case 'recordings':
        return (
          <DocSection id="recordings">
            <h3 className="text-lg font-semibold">Recordings API</h3>
            <p className="text-sm text-muted-foreground">
              Registrazioni delle chiamate. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/recordings</code>.
              Lettura/download: scope <code className="bg-muted px-1 rounded">recordings:access</code> (applicato globalmente al router).
              Cancellazione: richiede SIA <code className="bg-muted px-1 rounded">recordings:access</code> (router-level) SIA <code className="bg-muted px-1 rounded">voip:write</code> (route-level) — la API key deve avere entrambi gli scope.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/recordings"
              desc="Lista registrazioni con filtri opzionali. Query: extension, caller_number, date_from, date_to (ISO), limit (default 50), offset. Ordinata per created_at DESC."
              scope="recordings:access"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/recordings/:id"
              desc="Metadata di una registrazione (call_uuid, file_path, duration, size)."
              scope="recordings:access"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/recordings/:id/download"
              desc="Download file audio. Path validation contro allow-list (/var/lib/freeswitch/recordings, /usr/local/freeswitch/recordings, /opt/freeswitch/recordings, /tmp/recordings) — 403 ACCESS_DENIED su path traversal. Content-Type derivato dall'estensione (wav/mp3/ogg)."
              scope="recordings:access"
            />

            <h4 className="text-sm font-semibold mt-4">Delete</h4>
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/recordings/:id"
              desc="Elimina solo il record DB (il file su disco non viene rimosso da questa rotta). Richiede entrambi gli scope: recordings:access + voip:write."
              scope="recordings:access + voip:write"
            />

            <CodeBlock title="GET /recordings — Query parameters">{`GET /api/v2/voip/recordings?extension=1001&date_from=2026-05-01T00:00:00Z&date_to=2026-05-04T23:59:59Z&limit=50&offset=0
X-API-Key: sk_live_...

# Filtri server-side:
#   extension      : string  — match esatto sull'estensione owner della call
#   caller_number  : string  — match esatto sul caller number
#   date_from      : ISO 8601 — created_at >= date_from
#   date_to        : ISO 8601 — created_at <= date_to
#   limit          : number  — default 50
#   offset         : number  — default 0`}</CodeBlock>

            <CodeBlock title="GET /recordings — Response">{`{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "call_uuid": "fs-call-uuid",
      "extension": "1001",
      "caller_number": "+1234567890",
      "destination_number": "1001",
      "file_path": "/var/lib/freeswitch/recordings/2026/05/04/<call_uuid>.wav",
      "duration": 125,
      "file_size": 2456789,
      "created_at": "2026-05-04T10:00:00Z"
    }
  ],
  "meta": { "total": 1, "limit": 50, "offset": 0 }
}`}</CodeBlock>

            <CodeBlock title="GET /recordings/:id/download — Headers">{`HTTP/1.1 200 OK
Content-Type: audio/wav            // o audio/mpeg, audio/ogg
Content-Length: 2456789
Content-Disposition: attachment; filename="recording_<call_uuid>.wav"
Cache-Control: private, max-age=3600

<binary stream>`}</CodeBlock>
          </DocSection>
        );

      case 'voicemail':
        return (
          <DocSection id="voicemail">
            <h3 className="text-lg font-semibold">Voicemail API</h3>
            <p className="text-sm text-muted-foreground">
              Caselle e messaggi voicemail. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/voicemail</code>.
              Lettura: <code className="bg-muted px-1 rounded">voip:read</code>; mark-as-read e delete: <code className="bg-muted px-1 rounded">voip:write</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Boxes</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/voicemail/boxes"
              desc="Lista voicemail boxes del tenant con message_count e unread_count aggregati (esclusi messaggi soft-deleted)."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/voicemail/boxes/:id"
              desc="Dettaglio della casella per UUID interno."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/voicemail/boxes/:id/messages"
              desc="Lista messaggi di una casella (esclusi soft-deleted). Query: is_new (bool), is_read (bool), limit (default 50), offset. Ordinati per created_at DESC."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Messages</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/voicemail/messages/:id"
              desc="Dettaglio messaggio (verifica ownership via JOIN su voicemail_boxes.tenant_id)."
              scope="voip:read"
            />
            <Endpoint
              method="PUT"
              path="/api/v2/voip/voicemail/messages/:id/read"
              desc="Marca il messaggio come letto: is_read=true, is_new=false, read_at=NOW()."
              scope="voip:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/voicemail/messages/:id"
              desc="Soft delete: imposta is_deleted=true, deleted_at=NOW(). Il record fisico non viene rimosso."
              scope="voip:write"
            />

            <CodeBlock title="GET /voicemail/boxes — Response">{`{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "tenant_id": "uuid",
      "extension": "1001",
      "mailbox_number": "1001",
      "display_name": "John Doe",
      "pin": "***",
      "settings": { "email_notifications": true, "transcription": false },
      "message_count": 12,
      "unread_count": 3,
      "created_at": "2026-05-04T10:00:00Z"
    }
  ]
}`}</CodeBlock>

            <CodeBlock title="GET /voicemail/boxes/:id/messages — Query / Response">{`GET /api/v2/voip/voicemail/boxes/<id>/messages?is_new=true&limit=50

{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "voicemail_box_id": "uuid",
      "caller_number": "+1234567890",
      "caller_name": "Acme Corp",
      "duration": 28,
      "file_path": "/var/lib/freeswitch/voicemail/.../<id>.wav",
      "is_new": true,
      "is_read": false,
      "is_deleted": false,
      "transcription": null,
      "created_at": "2026-05-04T09:55:12Z",
      "read_at": null
    }
  ],
  "meta": { "total": 3, "limit": 50, "offset": 0 }
}`}</CodeBlock>
          </DocSection>
        );

      case 'cdr':
        return (
          <DocSection id="cdr">
            <h3 className="text-lg font-semibold">CDR (Call Detail Records) API</h3>
            <p className="text-sm text-muted-foreground">
              Base: <code className="bg-muted px-1 rounded">/api/v2/voip/cdr</code>.
              Lista, dettaglio e statistiche richiedono lo scope <code className="bg-muted px-1 rounded">voip:read</code> oppure <code className="bg-muted px-1 rounded">cdr:export</code>;
              l'endpoint di export richiede esplicitamente <code className="bg-muted px-1 rounded">cdr:export</code>.
            </p>
            <Endpoint method="GET" path="/api/v2/voip/cdr" desc="Lista CDR con filtri (vedi tabella). Restituisce data[] paginata e meta { total, limit, offset, timestamp }." scope="voip:read | cdr:export" />
            <Endpoint method="GET" path="/api/v2/voip/cdr/:id" desc="Dettaglio singolo CDR per call_uuid (NON UUID interno). 404 CDR_NOT_FOUND se non appartiene al tenant." scope="voip:read | cdr:export" />
            <Endpoint method="GET" path="/api/v2/voip/cdr/stats" desc="Aggregati per tenant nell'intervallo opzionale start_date/end_date: total_calls, answered, missed, total_duration, avg_duration, per_direction." scope="voip:read | cdr:export" />
            <Endpoint method="GET" path="/api/v2/voip/cdr/export?format=csv" desc="Export massivo (limite hard 10.000 record). format=csv (Content-Disposition attachment, filename con data) oppure format=json (default)." scope="cdr:export" />
            <h4 className="text-sm font-semibold mt-3">Query Parameters</h4>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Param</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">start_date</TableCell><TableCell>ISO 8601</TableCell><TableCell>Start date filter</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">end_date</TableCell><TableCell>ISO 8601</TableCell><TableCell>End date filter</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">call_direction</TableCell><TableCell>string</TableCell><TableCell>inbound | outbound | internal</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">caller_id_number</TableCell><TableCell>string</TableCell><TableCell>Filter by caller number</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">destination_number</TableCell><TableCell>string</TableCell><TableCell>Filter by destination</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">extension</TableCell><TableCell>string</TableCell><TableCell>Filter by internal extension</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">status</TableCell><TableCell>string</TableCell><TableCell>Accettato in input ma <strong>NON applicato</strong> dal route corrente (no-op). Per filtrare per esito chiamata usare <code>hangup_cause</code>; il valore di esito è esposto nel campo response <code>hangup_disposition</code> (answered | no_answer | busy | failed).</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">hangup_cause</TableCell><TableCell>string</TableCell><TableCell>FreeSWITCH hangup cause (es. NORMAL_CLEARING, USER_BUSY)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">ai_agent_id</TableCell><TableCell>UUID</TableCell><TableCell>Filtra solo le chiamate gestite dall'agente AI specificato (colonna popolata dal dispatcher AI)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">limit</TableCell><TableCell>number</TableCell><TableCell>Results per page (default 50)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">offset</TableCell><TableCell>number</TableCell><TableCell>Pagination offset</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
            <CodeBlock title="Response Example (con ai_agent_id top-level)">{`{
  "success": true,
  "data": [{
    "id": "uuid",
    "call_direction": "inbound",
    "caller_id_number": "+1234567890",
    "destination_number": "1001",
    "start_time": "2026-05-02T10:00:00Z",
    "duration": 125,
    "bill_sec": 120,                         // billable seconds (talk time)
    "hangup_disposition": "answered",        // answered | no_answer | busy | failed
    "hangup_cause": "NORMAL_CLEARING",
    "recording_id": "uuid",
    "ai_agent_id": "uuid-or-null",           // top-level (non più dentro metadata)
    "trunk_name": "Main Trunk",              // arricchito via JOIN su sip_trunks
    "trunk_did": "+390212345678"
  }],
  "meta": { "total": 100, "limit": 50, "offset": 0, "timestamp": "2026-05-04T10:00:00Z" }
}`}</CodeBlock>
            <p className="text-xs text-muted-foreground">Il campo <code>ai_agent_id</code> è esposto come colonna top-level (non più dentro <code>metadata</code>) per consentire filter/join diretti dalle integrazioni di analytics e CRM.</p>

            <CodeBlock title="GET /cdr/stats — Response">{`{
  "success": true,
  "data": {
    "total_calls": 1250,
    "answered_calls": 980,                    // hangup_disposition='answered'
    "missed_calls": 270,                      // hangup_disposition IN ('no_answer','busy')
    "total_duration": 184500,                 // somma duration (secondi)
    "avg_duration": 188.4,                    // AVG(duration) FILTER (WHERE duration > 0)
    "avg_mos": 4.21                           // AVG(mos_score) WHERE NOT NULL — opzionale, omesso se nessuna riga ha mos_score
  },
  "meta": { "timestamp": "2026-05-04T10:00:00Z" }
}
// NB: nessun by_direction nell'implementazione attuale; per aggregati per direzione filtrare /cdr con call_direction.`}</CodeBlock>

            <CodeBlock title="GET /cdr/:id — Lookup per call_uuid">{`GET /api/v2/voip/cdr/<call_uuid>
X-API-Key: sk_live_...

# 200 Response: stesso shape di un singolo elemento di /cdr.data[]
# 404 Response:
{ "success": false, "error": "CDR record not found", "error_code": "CDR_NOT_FOUND" }`}</CodeBlock>

            <CodeBlock title="GET /cdr/export — CSV">{`GET /api/v2/voip/cdr/export?format=csv&start_date=2026-05-01T00:00:00Z&end_date=2026-05-04T23:59:59Z
X-API-Key: sk_live_...

HTTP/1.1 200 OK
Content-Type: text/csv
Content-Disposition: attachment; filename="cdr_export_2026-05-04.csv"

call_uuid,start_time,call_direction,caller_id_number,destination_number,duration,billsec,status,hangup_cause,ai_agent_id
...

# Hard limit lato server: 10.000 record per export.
# format=json (default) ritorna { success, data, meta: { total, exported, format, timestamp } }.`}</CodeBlock>
          </DocSection>
        );

      case 'legacy-migration':
        return (
          <DocSection id="legacy-migration">
            <h3 className="text-lg font-semibold">Migrazione dal pacchetto legacy integration-api</h3>
            <p className="text-sm text-muted-foreground">
              Il servizio separato <code className="bg-muted px-1 rounded">integration-api</code> è stato ritirato: le API per gli integratori
              vivono nel backend sotto <code className="bg-muted px-1 rounded">/api/v2</code> con autenticazione <code className="bg-muted px-1 rounded">X-API-Key</code>.
              Ogni funzione legacy ha un equivalente v2; le differenze sono di <strong>modello</strong>, non buchi funzionali.
              La specifica OpenAPI completa è su <code className="bg-muted px-1 rounded">/api-docs</code> e <code className="bg-muted px-1 rounded">/api-docs.json</code>.
            </p>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Legacy integration-api</TableHead><TableHead>API v2</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
                <TableBody>
                  {LEGACY_TO_V2_ROWS.map((row) => (
                    <TableRow key={row.legacy}>
                      <TableCell className="font-mono text-xs align-top">{row.legacy}</TableCell>
                      <TableCell className="font-mono text-xs align-top">{row.v2}</TableCell>
                      <TableCell className="text-sm">{row.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <CodeBlock title="Esempio: configurazione AI che prima stava sul trunk">{`# Legacy (trunk): { "aiAgentRef": "agent-1", "aiFailoverExtension": "1000", "aiTimePolicy": "office-hours" }
# v2: la configurazione vive sulla rotta inbound del DID
POST /api/v2/voip/inbound-routes
{
  "name": "Centralino AI",
  "did_number": "+390212345678",
  "destination_type": "internal_ai_agent",
  "destination_id": "<uuid agente>",
  "time_condition_id": "<uuid time condition 'office-hours'>",
  "failover_destination_type": "extension",
  "failover_destination_id": "<uuid interno 1000>"
}`}</CodeBlock>
          </DocSection>
        );

    default:
      return null;
  }
}
